import { http } from "@/api/http";
import type {
  DocumentBlock,
  DocumentAction,
  DocumentEditRequest,
  DocumentPack,
  DocumentPackSummary,
  DocumentType,
  DocumentWorkflowSnapshot,
  GenerateDocumentPackRequest,
} from "@/types/documents";
import { assertUnsignedDecimal } from "@/utils/unsignedDecimal";

const PDF_EXTRACTION_TIMEOUT_MS = 60_000;
const DOCUMENT_AI_TIMEOUT_MS = 300_000;

export type DocumentPrintOrientation = "portrait" | "landscape";
export type DocumentPrintPaperSize = "a4" | "letter";
export type DocumentPrintMargin = "normal" | "narrow" | "wide";
export type DocumentPrintColor = "color" | "grayscale";
export type DocumentPrintScaleMode = "printable-area" | "actual" | "custom";
export type DocumentPrintPagesPerSheet = 1 | 2 | 4 | 6 | 9 | 16;

export interface DocumentPrintSettings {
  orientation: DocumentPrintOrientation;
  paper_size: DocumentPrintPaperSize;
  margin: DocumentPrintMargin;
  color: DocumentPrintColor;
  page_range: string;
  scale_mode: DocumentPrintScaleMode;
  scale_percent: number;
  pages_per_sheet: DocumentPrintPagesPerSheet;
  headers_footers: boolean;
  background_graphics: boolean;
}

export async function listDocumentPacks(signal?: AbortSignal): Promise<DocumentPackSummary[]> {
  const response = signal
    ? await http.get<{ document_packs: DocumentPackSummary[] }>("/document-packs", { signal })
    : await http.get<{ document_packs: DocumentPackSummary[] }>("/document-packs");
  return response.data.document_packs ?? [];
}

export async function createDocumentWorkflow(
  payload: GenerateDocumentPackRequest,
  signal?: AbortSignal,
): Promise<DocumentWorkflowSnapshot> {
  const response = await http.post<{ workflow: DocumentWorkflowSnapshot }>(
    "/document-workflows",
    payload,
    { ...(signal ? { signal } : {}) },
  );
  return response.data.workflow;
}

export async function getDocumentWorkflow(
  workflowId: string,
  afterSequence: string,
  signal?: AbortSignal,
): Promise<DocumentWorkflowSnapshot> {
  const cursor = assertUnsignedDecimal(afterSequence);
  const path = `/document-workflows/${encodeURIComponent(workflowId)}?after_sequence=${cursor}`;
  const response = signal
    ? await http.get<{ workflow: DocumentWorkflowSnapshot }>(path, { signal })
    : await http.get<{ workflow: DocumentWorkflowSnapshot }>(path);
  return response.data.workflow;
}

export async function getActiveDocumentWorkflow(
  signal?: AbortSignal,
): Promise<DocumentWorkflowSnapshot | null> {
  const response = signal
    ? await http.get<{ workflow: DocumentWorkflowSnapshot | null }>(
        "/document-workflows/active",
        { signal },
      )
    : await http.get<{ workflow: DocumentWorkflowSnapshot | null }>(
        "/document-workflows/active",
      );
  return response.data.workflow;
}

export async function cancelDocumentWorkflow(
  workflowId: string,
  signal?: AbortSignal,
): Promise<DocumentWorkflowSnapshot> {
  const path = `/document-workflows/${encodeURIComponent(workflowId)}`;
  const response = signal
    ? await http.delete<{ workflow: DocumentWorkflowSnapshot }>(path, { signal })
    : await http.delete<{ workflow: DocumentWorkflowSnapshot }>(path);
  return response.data.workflow;
}

export async function extractPdfText(data: string, signal?: AbortSignal): Promise<{
  text: string;
  repaired?: boolean;
  repaired_pages?: number;
  structure_repaired?: boolean;
}> {
  const response = await http.post<{
    text: string;
    repaired?: boolean;
    repaired_pages?: number;
    structure_repaired?: boolean;
  }>("/files/extract-pdf", { data }, {
    timeout: PDF_EXTRACTION_TIMEOUT_MS,
    ...(signal ? { signal } : {}),
  });
  return response.data;
}

export async function getDocumentPack(packId: string, signal?: AbortSignal): Promise<DocumentPack> {
  const url = `/document-packs/${encodeURIComponent(packId)}`;
  const response = signal
    ? await http.get<{ document_pack: DocumentPack }>(url, { signal })
    : await http.get<{ document_pack: DocumentPack }>(url);
  return response.data.document_pack;
}

export async function getDocumentPdf(
  packId: string,
  documentType: DocumentType,
  version: number,
  settings: DocumentPrintSettings,
  signal?: AbortSignal,
): Promise<Blob> {
  const query = new URLSearchParams({
    version: String(version),
    orientation: settings.orientation,
    paper_size: settings.paper_size,
    margin: settings.margin,
    color: settings.color,
    page_range: settings.page_range,
    scale_mode: settings.scale_mode,
    scale_percent: String(settings.scale_percent),
    pages_per_sheet: String(settings.pages_per_sheet),
    headers_footers: String(settings.headers_footers),
    background_graphics: String(settings.background_graphics),
  });
  const url = `/document-packs/${encodeURIComponent(packId)}/documents/${encodeURIComponent(documentType)}/pdf?${query.toString()}`;
  const response = await http.get<Blob>(url, {
    responseType: "blob",
    ...(signal ? { signal } : {}),
  });
  return response.data;
}

export async function saveDocumentContent(
  packId: string,
  documentType: DocumentType,
  blocks: DocumentBlock[],
  signal?: AbortSignal,
): Promise<DocumentPack> {
  const url = `/document-packs/${encodeURIComponent(packId)}/content`;
  const payload = { document_type: documentType, blocks };
  const response = signal
    ? await http.post<{ document_pack: DocumentPack }>(url, payload, { signal })
    : await http.post<{ document_pack: DocumentPack }>(url, payload);
  return response.data.document_pack;
}
export async function editDocument(
  packId: string,
  payload: DocumentEditRequest,
  signal?: AbortSignal,
): Promise<DocumentPack> {
  const response = await http.post<{ document_pack: DocumentPack }>(
    `/document-packs/${encodeURIComponent(packId)}/edit`,
    payload,
    { timeout: DOCUMENT_AI_TIMEOUT_MS, ...(signal ? { signal } : {}) },
  );
  return response.data.document_pack;
}
export async function runDocumentAction(
  packId: string,
  action: DocumentAction,
  signal?: AbortSignal,
): Promise<DocumentPack> {
  const requestConfig = ["optimise-cv", "refresh-jd-keywords"].includes(action)
    ? { timeout: DOCUMENT_AI_TIMEOUT_MS, ...(signal ? { signal } : {}) }
    : { ...(signal ? { signal } : {}) };
  const response = await http.post<{ document_pack: DocumentPack }>(
    `/document-packs/${encodeURIComponent(packId)}/${action}`,
    {},
    requestConfig,
  );
  return response.data.document_pack;
}

export async function restoreDocumentVersion(
  packId: string,
  version: number,
  signal?: AbortSignal,
): Promise<DocumentPack> {
  const response = await http.post<{ document_pack: DocumentPack }>(
    `/document-packs/${encodeURIComponent(packId)}/restore-version`,
    { version },
    { ...(signal ? { signal } : {}) },
  );
  return response.data.document_pack;
}
